import { OrganizationNodeProps } from '@ixicare/resources';

export interface OrgOption {
  value: string;
  label: string;
}

// Flatten the org tree into a single, indented option list for the selector.
// Watcherr employees see every top-level org (multiple roots); org admins see
// their single scoped root and can narrow to any sub-org below it.
export function buildOrgOptions(roots: OrganizationNodeProps[]): OrgOption[] {
  const options: OrgOption[] = [];
  const walk = (node: OrganizationNodeProps, depth: number) => {
    options.push({ value: node.id, label: `${'  '.repeat(depth)}${node.name}` });
    [...node.children].sort((a, b) => a.name.localeCompare(b.name)).forEach((child) => walk(child, depth + 1));
  };
  roots.forEach((root) => walk(root, 0));
  return options;
}

export function findOrgNode(roots: OrganizationNodeProps[], id: string): OrganizationNodeProps | undefined {
  for (const root of roots) {
    if (root.id === id) return root;
    const found = findOrgNode(root.children, id);
    if (found) return found;
  }
  return undefined;
}

// Sentinel selector value for the synthetic "no organization" bucket. Only
// offered to unscoped (Watcherr) employees; org admins never see non-org sites.
export const NO_ORG_VALUE = '__no_org__';

export type BillingRowScope = { organizationUuid: string } | { siteUuid: string };

export interface BillingNodeRow {
  key: string;
  kind: 'org' | 'site';
  name: string;
  depth: number;
  scope: BillingRowScope;
}

// Depth-first walk producing the table rows: an org row (subtree total) followed
// by its direct site rows, then recursively each sub-org and its sites.
export function flattenBillingRows(node: OrganizationNodeProps, depth = 0): BillingNodeRow[] {
  const rows: BillingNodeRow[] = [
    { key: `org-${node.id}`, kind: 'org', name: node.name, depth, scope: { organizationUuid: node.id } },
  ];
  [...node.sites]
    .sort((a, b) => a.name.localeCompare(b.name))
    .forEach((site) => {
      rows.push({
        key: `site-${site.uuid}`,
        kind: 'site',
        name: site.name,
        depth: depth + 1,
        scope: { siteUuid: site.uuid },
      });
    });
  [...node.children]
    .sort((a, b) => a.name.localeCompare(b.name))
    .forEach((child) => {
      rows.push(...flattenBillingRows(child, depth + 1));
    });
  return rows;
}

// Site-only rows for the "no organization" bucket — no aggregate row, since
// these sites share no org entity to total under.
export function buildUnassignedSiteRows(sites: { uuid: string; name: string }[]): BillingNodeRow[] {
  return [...sites]
    .sort((a, b) => a.name.localeCompare(b.name))
    .map((site) => ({
      key: `site-${site.uuid}`,
      kind: 'site' as const,
      name: site.name,
      depth: 0,
      scope: { siteUuid: site.uuid },
    }));
}
